import React, { ReactElement, useEffect, useState } from 'react';
import classnames from 'classnames';

import { SearchInput } from '../search-input/search-input';
import DefaultButton from '../default-button/default-button';
import { MINIMAL_SEARCH_LENGTH, SEARCH_TERM_MIN_LENGTH } from '../../../constants/common.constants';
import './controlled-multi-selection-dropdown.scss';
import { FormattedMessage } from 'react-intl';
import { MultiSelectOption } from '../multi-select-list/_multi-select-option';
import Text from '../text/text';
import { LOADING } from '../../../constants/common-labels.constants';

export interface IItemProps {
    id: string;
    label: string;
}

export interface IProps {
    entityName: string | ReactElement;
    onApply: (items: IItemProps[]) => void;
    classNames?: string | null;
    onFetchItems: ((query?: string) => Promise<IItemProps[]>) | ((query?: string) => IItemProps[]);
    noItemsPlaceholder?: ReactElement;
    withBESearch?: boolean;
    noSearchResultText?: string | ReactElement;
    value: IItemProps[];
    defaultValue?: IItemProps[];
    withIcon?: ReactElement;
    withoutHighlight?: boolean;
    withDebounce?: boolean;
}

const ControlledMultiSelectionDropdown = (props: IProps) => {
    const {
        entityName,
        classNames,
        onApply,
        onFetchItems,
        noItemsPlaceholder,
        withBESearch = false,
        noSearchResultText,
        withIcon,
        withoutHighlight,
        value,
        defaultValue,
        withDebounce,
    } = props;


    const [isOpen, toggleOptions] = useState(false);
    const [isFetching, setFetching] = useState(false);
    const [items, setItems] = useState<IItemProps[]>([]);
    const [selectedItems, setSelectedItems] = useState<IItemProps[]>([]);
    const [appliedItems, setAppliedItems] = useState<IItemProps[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchItems = async (query?: string) => {
        setFetching(true);
        const response = await onFetchItems(query);
        setItems(response);
        setFetching(false);
    };

    const onDropdownClick = () => {
        setSearchTerm('');
        toggleOptions(!isOpen);

        if (!isOpen) {
            if (onFetchItems) fetchItems();
            setSelectedItems(appliedItems);
        }
    };

    const onOptionClick = (clickedItem: IItemProps) => {
        const isSelected = selectedItems.find(item => item.id === clickedItem.id);

        if (isSelected) {
            setSelectedItems(selectedItems.filter(item => item.id !== clickedItem.id));
        } else {
            setSelectedItems([...selectedItems, clickedItem]);
        }
    };

    const onReset = () => {
        setSelectedItems(defaultValue || []);
    };

    const onApplyAction = () => {
        toggleOptions(false);
        setAppliedItems(selectedItems);

        onApply(selectedItems);
        setItems([]);
    };

    const trimmedSearchTerm = searchTerm.trim();
    const filteredItems = (trimmedSearchTerm.length < SEARCH_TERM_MIN_LENGTH) ? items :
        items.filter(item => {
            return item.label.toLowerCase().includes(trimmedSearchTerm.toLowerCase());
        });

    const entityNameUpper = `${(entityName as string).charAt(0).toUpperCase()}${(entityName as string).slice(1)}`;
    const dropDownLabel = appliedItems.length ? `${entityNameUpper}: ${appliedItems.length}` : entityNameUpper;

    useEffect(() => {
        if (withBESearch && (!searchTerm || searchTerm.trim().length >= MINIMAL_SEARCH_LENGTH)) {
            fetchItems(searchTerm);
        }
    }, [searchTerm]);

    useEffect(() => {
        setAppliedItems(value);
        setSelectedItems(value);
    }, [value]);

    const isResetButtonDisabled = selectedItems.length === (defaultValue || []).length;

    return (
        <div className={classnames('controlled-multi-selection-dropdown', classNames)}>
            <div
                onClick={onDropdownClick}
                className={classnames('cell-header-btn', { 'cell-header-btn--active': isOpen || (!withoutHighlight && appliedItems.length) })}
            >
                {withIcon ?? <Text message={dropDownLabel} withEllipsis />}
            </div>
            {isOpen &&
                <>
                    <div className='controlled-multi-selection-dropdown__overlay' onClick={() => toggleOptions(false)} />
                    <div className='controlled-multi-selection-dropdown__options'>
                        {withBESearch && <div className='controlled-multi-selection-dropdown__search'>
                            <SearchInput
                                placeholder={`Search ${entityNameUpper}`}
                                onChange={e => setSearchTerm(e.target.value)}
                                withDebounce={withDebounce}
                            />
                        </div>
                        }
                        <>
                            {(items.length || searchTerm) ?
                                <>
                                    <div className='controlled-multi-selection-dropdown__list'>
                                        {filteredItems.length ?
                                            filteredItems.map(item => {
                                                const isSelected = !!selectedItems.find(el => el.id === item.id);
                                                return (
                                                    <div
                                                        key={item.id}
                                                        onClick={() => onOptionClick(item)}
                                                        className={classnames('controlled-multi-selection-dropdown__option', {
                                                            'controlled-multi-selection-dropdown__option--selected': isSelected
                                                        })}
                                                    >
                                                        <Text
                                                            tag='div'
                                                            className='controlled-multi-selection-dropdown__option-label'
                                                            message={item.label}
                                                            withEllipsis
                                                        />
                                                        <MultiSelectOption
                                                            isSelected={isSelected}
                                                        />
                                                    </div>
                                                );
                                            }) : <div className='controlled-multi-selection-dropdown__placeholder'>
                                                {noSearchResultText || `No ${entityName} found.`}
                                            </div>
                                        }
                                    </div>
                                    <div className='controlled-multi-selection-dropdown__button-bar'>
                                        <DefaultButton
                                            modifier='cancel'
                                            text='Reset'
                                            onClick={onReset}
                                            className='read-only-textarea__controls-button'
                                            disabled={isResetButtonDisabled}
                                        />
                                        <DefaultButton
                                            modifier='confirm-safe'
                                            text='Apply'
                                            onClick={onApplyAction}
                                            className='read-only-textarea__controls-button'
                                        />
                                    </div>
                                </> : isFetching ? (
                                    <div className='controlled-multi-selection-dropdown__placeholder'>
                                        <FormattedMessage
                                            {...LOADING}
                                            values={{
                                                suffix: '...'
                                            }}
                                        />
                                    </div>
                                ) : noItemsPlaceholder
                            }
                        </>
                    </div>
                </>
            }
        </div>
    );
};

export default ControlledMultiSelectionDropdown;
